import { useEffect, useRef, useState } from "react";

import * as service from "../services/GroupMemberPopsService";

const createInitialPagination = (limit) => ({
  limit,
  offset: 0,
  total: 0,
  hasMore: false,
});

const normalizePops = (items) => (Array.isArray(items) ? items : []);

const mergeUniquePops = (current, incoming) => {
  const popMap = new Map();

  [...current, ...incoming].forEach((pop) => {
    const identity = pop?.popTag ?? pop?.popId;

    if (identity != null) {
      popMap.set(String(identity), pop);
    }
  });

  return Array.from(popMap.values());
};

const parsePopsResponse = (response, requestedLimit, requestedOffset) => {
  const data = response?.data;

  if (Array.isArray(data)) {
    return {
      pops: data,
      pagination: {
        limit: requestedLimit,
        offset: requestedOffset,
        total: requestedOffset + data.length,
        hasMore: data.length === requestedLimit,
      },
    };
  }

  const pops = normalizePops(data?.pops ?? data?.popThumbs ?? data?.items);

  const responsePagination = data?.pagination || {};

  const limit =
    Number(responsePagination.limit ?? data?.limit) || requestedLimit;

  const offset =
    Number(responsePagination.offset ?? data?.offset) || requestedOffset;

  const total = Number(responsePagination.total ?? data?.total) || 0;

  const hasMore =
    typeof responsePagination.hasMore === "boolean"
      ? responsePagination.hasMore
      : typeof data?.hasMore === "boolean"
        ? data.hasMore
        : total > 0
          ? offset + pops.length < total
          : pops.length === requestedLimit;

  return {
    pops,
    pagination: {
      limit,
      offset,
      total,
      hasMore,
    },
  };
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export const useUserGroupPops = (groupTag, limit = 12) => {
  const pageLimit = Math.max(1, Number(limit) || 12);

  const [state, setState] = useState({
    groupTag,
    limit: pageLimit,
    popThumbs: [],
    pop: null,
    pagination: createInitialPagination(pageLimit),
    loading: Boolean(groupTag),
    loadingMore: false,
    error: "",
  });

  const requestVersionRef = useRef(0);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);

  const isCurrent = state.groupTag === groupTag && state.limit === pageLimit;

  const current = isCurrent
    ? state
    : {
        groupTag,
        limit: pageLimit,
        popThumbs: [],
        pop: null,
        pagination: createInitialPagination(pageLimit),
        loading: Boolean(groupTag),
        loadingMore: false,
        error: "",
      };

  const getUserGroupPopThumbs = async () => {
    if (!groupTag) return false;

    const requestVersion = ++requestVersionRef.current;

    loadingRef.current = true;
    loadingMoreRef.current = false;

    setState({
      groupTag,
      limit: pageLimit,
      popThumbs: [],
      pop: null,
      pagination: createInitialPagination(pageLimit),
      loading: true,
      loadingMore: false,
      error: "",
    });

    try {
      const response = await service.getUserGroupPopThumbs(
        groupTag,
        pageLimit,
        0,
      );

      if (requestVersion !== requestVersionRef.current) {
        return false;
      }

      const parsed = parsePopsResponse(response, pageLimit, 0);

      setState((previous) => ({
        ...previous,
        popThumbs: parsed.pops,
        pagination: parsed.pagination,
        loading: false,
        error: "",
      }));

      return parsed.pops;
    } catch (error) {
      if (requestVersion !== requestVersionRef.current) {
        return false;
      }

      setState((previous) => ({
        ...previous,
        popThumbs: [],
        pagination: createInitialPagination(pageLimit),
        loading: false,
        error: getErrorMessage(error, "Failed to load group Pops."),
      }));

      return false;
    } finally {
      if (requestVersion === requestVersionRef.current) {
        loadingRef.current = false;
      }
    }
  };

  useEffect(() => {
    if (!groupTag) {
      requestVersionRef.current += 1;

      setState({
        groupTag,
        limit: pageLimit,
        popThumbs: [],
        pop: null,
        pagination: createInitialPagination(pageLimit),
        loading: false,
        loadingMore: false,
        error: "",
      });

      return;
    }

    void getUserGroupPopThumbs();

    return () => {
      requestVersionRef.current += 1;
      loadingRef.current = false;
      loadingMoreRef.current = false;
    };
  }, [groupTag, pageLimit]);

  const loadMoreUserGroupPops = async () => {
    if (
      !groupTag ||
      loadingRef.current ||
      loadingMoreRef.current ||
      !isCurrent ||
      !current.pagination.hasMore
    ) {
      return false;
    }

    const nextOffset =
      Number(current.pagination.offset || 0) +
      Number(current.pagination.limit || pageLimit);

    const requestVersion = requestVersionRef.current;

    loadingMoreRef.current = true;

    setState((previous) => ({
      ...previous,
      loadingMore: true,
      error: "",
    }));

    try {
      const response = await service.getUserGroupPopThumbs(
        groupTag,
        pageLimit,
        nextOffset,
      );

      if (requestVersion !== requestVersionRef.current) {
        return false;
      }

      const parsed = parsePopsResponse(response, pageLimit, nextOffset);

      setState((previous) => ({
        ...previous,
        popThumbs: mergeUniquePops(previous.popThumbs, parsed.pops),
        pagination: parsed.pagination,
        loadingMore: false,
      }));

      return parsed.pops;
    } catch (error) {
      if (requestVersion !== requestVersionRef.current) {
        return false;
      }

      setState((previous) => ({
        ...previous,
        loadingMore: false,
        error: getErrorMessage(error, "Failed to load more Pops."),
      }));

      return false;
    } finally {
      if (requestVersion === requestVersionRef.current) {
        loadingMoreRef.current = false;
      }
    }
  };

  const getUserGroupPop = async (popTag) => {
    if (!groupTag || !popTag) return false;

    const requestVersion = requestVersionRef.current;

    try {
      const response = await service.getUserGroupPop(groupTag, popTag);

      if (requestVersion !== requestVersionRef.current) {
        return false;
      }

      const data = response?.data ?? null;

      setState((previous) => ({
        ...previous,
        pop: data,
      }));

      return data;
    } catch (error) {
      if (requestVersion !== requestVersionRef.current) {
        return false;
      }

      setState((previous) => ({
        ...previous,
        pop: null,
        error: getErrorMessage(error, "Failed to load Pop."),
      }));

      return false;
    }
  };

  const resetUserGroupPops = () => {
    requestVersionRef.current += 1;
    loadingRef.current = false;
    loadingMoreRef.current = false;

    setState({
      groupTag,
      limit: pageLimit,
      popThumbs: [],
      pop: null,
      pagination: createInitialPagination(pageLimit),
      loading: false,
      loadingMore: false,
      error: "",
    });
  };

  return {
    popThumbs: current.popThumbs,
    pop: current.pop,
    pagination: current.pagination,
    loading: current.loading,
    loadingMore: current.loadingMore,
    error: current.error,
    hasMore: current.pagination.hasMore,

    setPopThumbs: (value) => {
      setState((previous) => ({
        ...previous,
        popThumbs:
          typeof value === "function" ? value(previous.popThumbs) : value,
      }));
    },

    setPop: (value) => {
      setState((previous) => ({
        ...previous,
        pop: typeof value === "function" ? value(previous.pop) : value,
      }));
    },

    setError: (value) => {
      setState((previous) => ({
        ...previous,
        error: value,
      }));
    },

    getUserGroupPopThumbs,
    loadMoreUserGroupPops,
    getUserGroupPop,
    resetUserGroupPops,
  };
};

export default useUserGroupPops;
